import React, { useEffect, useState } from 'react';
import { tokens, type } from '../theme';
import { SEASONS, AGE_GROUPS, U15_PLUS, QOP_AGE_GROUPS, shortDivision, DEFAULT_SEASON, DEFAULT_AGE_GROUP, DEFAULT_DIVISION_NAME, isSeasonInProgress } from '../config/seasons';
import { Link, useSearchParams } from 'react-router-dom';
import FilterBar from '../components/FilterBar';
import { TAP_TARGET } from '../utils/tapTarget';
import { getStandings, getConferences, getConferencesByDivision, getDivisions, fetchSeasons } from '../services/api';

import { 
  Box, 
  Button,
  Container, 
  Grid,
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TableSortLabel,
  Typography,
  CircularProgress,
  Chip,
  useMediaQuery,
  useTheme,
  Avatar,
  OutlinedInput,
  Tooltip
} from '@mui/material';

const Standings = () => {
  const theme = useTheme();
  
  // Add error boundary state
  const [error, setError] = useState(null);
  
  const [standings, setStandings] = useState([]);
  
  const [conferences, setConferences] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters are mirrored into the URL so a standings view can be shared by link.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(searchParams.get('age_group') || DEFAULT_AGE_GROUP);
  const [selectedConference, setSelectedConference] = useState(searchParams.get('conference') || 'all');
  const [selectedDivision, setSelectedDivision] = useState(searchParams.get('division') || DEFAULT_DIVISION_NAME);
  const [selectedSeason, setSelectedSeason] = useState(searchParams.get('season') || DEFAULT_SEASON);
  // Season list comes from the API so a new season needs no frontend deploy;
  // SEASONS is the build-time fallback.
  const [seasons, setSeasons] = useState(SEASONS);
  useEffect(() => {
    let active = true;
    fetchSeasons()
      .then(data => { if (active && data?.seasons?.length) setSeasons(data.seasons); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const [selectedCompetition, setSelectedCompetition] = useState(searchParams.get('competition') || 'League');
  
  // Keep the URL in step with the active filters (replace, so we don't spam history).
  useEffect(() => {
    const next = new URLSearchParams();
    next.set('season', selectedSeason);
    next.set('age_group', selectedAgeGroup);
    next.set('division', selectedDivision);
    next.set('conference', selectedConference);
    next.set('competition', selectedCompetition);
    setSearchParams(next, { replace: true });
  }, [selectedSeason, selectedAgeGroup, selectedDivision, selectedConference, selectedCompetition, setSearchParams]);

  // Divisions are per season, so a division carried over from another season may not
  // exist in the selected one — which showed "No teams found" with a blank Division
  // select (26/27 currently has Academy Division only). Reconcile once they load.
  useEffect(() => {
    if (!Array.isArray(divisions) || divisions.length === 0) return;
    if (selectedDivision === 'all') return;
    if (divisions.some(d => d.division_name === selectedDivision)) return;
    const preferred =
      divisions.find(d => d.division_name === DEFAULT_DIVISION_NAME) || divisions[0];
    if (preferred) setSelectedDivision(preferred.division_name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisions]);

  const [sortColumn, setSortColumn] = useState('PTS');
  const [sortDirection, setSortDirection] = useState('desc');
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // On a phone the full 15-column table is ~929px wide, so PTS — the number the
  // table exists for — sat off-screen behind six columns of horizontal scroll.
  // Default to an essential column set and let the full detail be revealed on tap.
  const [showAllStats, setShowAllStats] = useState(false);
  const compact = isMobile && !showAllStats;
  // Flex resolves a level scoreline with penalties, so its tables carry a shootout
  // record; League's do not and the column is left out entirely rather than filled
  // with dashes. Driven by the rows themselves rather than the filter, so a table
  // that has not loaded yet does not flash a column in and out.
  const showPk = standings.some((t) => t.pk_wins !== null && t.pk_wins !== undefined);
  const secondaryCol = { display: compact ? 'none' : 'table-cell' };
  // Desktop column widths. U13/U14 carry three more columns than U15+ (Att, Def,
  // QoP), and the old widths summed to 1234px against a 1080px content column,
  // so on a 1280px laptop QoP and Last 5 were pushed off-screen entirely — not
  // hidden by a rule, just unreachable without a scrollbar nobody saw. These
  // Ratios rather than hard widths: tableLayout is 'fixed' with width 100%,
  // so these divide the panel rather than sum to it.
  const W_STAT = 46;     // MP, W, D, L, GF, GA, CS — at most 3 digits
  const W_RATE = 58;     // DIFF, GF/M, GA/M — signed or decimal
  const W_SCORE = 54;    // PTS, Att, Def, QoP
  const W_FORM = 124;    // Last 5 — five 18px badges plus gaps


  // Right-edge fade whenever the table is wider than its container, so it reads as
  // scrollable rather than cut off. (Tracking scroll position to hide it at the end
  // proved unreliable and isn't worth the complexity for a hint.)
  const scrollerRef = React.useRef(null);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateScrollHint = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollRight(el.scrollWidth > el.clientWidth + 4);
  }, []);
  useEffect(() => {
    updateScrollHint();
    window.addEventListener('resize', updateScrollHint);
    return () => window.removeEventListener('resize', updateScrollHint);
  }, [updateScrollHint, showAllStats, standings]);

  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Removed problematic mobile initialization that was causing unnecessary re-renders
  
  // Removed problematic useEffect that was re-ranking standings
  // The backend already handles ranking correctly
  
  // Load divisions when season changes
  useEffect(() => {
    const loadDivisions = async () => {
      try {
        const divisionsData = await getDivisions(selectedSeason).catch(err => {
          console.error('Error loading divisions:', err);
          return [];
        });
        setDivisions(Array.isArray(divisionsData) ? divisionsData : []);
      } catch (error) {
        console.error('Error loading divisions:', error);
        setDivisions([]);
      }
    };
    
    loadDivisions();
  }, [selectedSeason]);
  
  // Load conferences when division or season changes (after divisions are loaded)
  useEffect(() => {
    const loadConferences = async () => {
      if (divisions.length === 0) return; // Don't load if divisions aren't ready yet
      
      try {
        let divisionId = null;
        if (selectedDivision !== 'all' && Array.isArray(divisions) && divisions.length > 0) {
          const division = divisions.find(div => div.division_name === selectedDivision);
          divisionId = division ? division.id : null;
        }
        
        const conferencesData = divisionId ? await getConferencesByDivision(divisionId, selectedSeason, selectedCompetition) : await getConferences(selectedSeason, null, selectedCompetition).catch(err => {
          console.error('Error loading conferences:', err);
          return [];
        });
        setConferences(Array.isArray(conferencesData) ? conferencesData : []);
      } catch (error) {
        console.error('Error loading conferences:', error);
        setConferences([]);
      }
    };

    loadConferences();
    // selectedCompetition included: Flex names its conferences differently from
    // League, so the list has to be refetched when the competition changes.
  }, [selectedDivision, selectedSeason, selectedCompetition, divisions]);
  
  useEffect(() => {
    const loadStandings = async () => {
      // Only load standings if divisions are loaded (or if we're showing all divisions)
      if (selectedDivision !== 'all' && (!Array.isArray(divisions) || divisions.length === 0)) {
        return;
      }
      
      setLoading(true);
      try {
        const conferenceId = selectedConference !== 'all' ? selectedConference : undefined;
        // Find the division ID from the selected name
        let divisionId = undefined;
        if (selectedDivision !== 'all' && Array.isArray(divisions) && divisions.length > 0) {
          const division = divisions.find(div => div.division_name === selectedDivision);
          divisionId = division ? division.id : undefined;
          
          // If we can't find the division, don't proceed
          if (!divisionId) {
            setStandings([]);
            setLoading(false);
            return;
          }
        }
        
        const data = await getStandings(selectedAgeGroup, conferenceId, divisionId, selectedSeason, selectedCompetition);
        const standingsData = Array.isArray(data) ? data : [];
        
        // Apply sorting and recalculate rankings based on current sort settings
        // The backend returns data sorted by points, but we may need to re-sort based on user selection
        // For now, we'll just ensure rankings are correct (backend already sorts by points with tiebreakers)
        let sortedData = [...standingsData];
        
        // Always recalculate rankings to ensure they're sequential
        sortedData = sortedData.map((team, index) => ({
          ...team,
          Rk: index + 1
        }));
        
        // If user has changed sort from default, apply that sort
        // Otherwise, data is already sorted by backend with tiebreakers
        // The rows arrive already ordered by points and the backend's full
        // tiebreaker chain, so there is nothing to redo here. This used to
        // re-apply a head-to-head swap the server had already applied, reading a
        // per-fixture records array that was sent for that purpose alone.
        setStandings(sortedData);
      } catch (error) {
        console.error('Error loading standings:', error);
        setError(error.message);
        setStandings([]); // Set empty array on error to prevent blank state
      } finally {
        setLoading(false);
      }
    };
    
    loadStandings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgeGroup, selectedConference, selectedDivision, selectedSeason, selectedCompetition, divisions]);

  
  const handleAgeGroupChange = (e) => {
    const newAgeGroup = e.target.value;
    setSelectedAgeGroup(newAgeGroup);
    
    // Check if age group is U15+ (supports competition filter)
    const u15PlusGroups = U15_PLUS;
    const hasU15Plus = u15PlusGroups.includes(newAgeGroup);
    
    // If not U15+, reset competition to 'League' (default)
    if (!hasU15Plus) {
      setSelectedCompetition('League');
    }
  };
  
  const handleConferenceChange = (e) => {
    setSelectedConference(e.target.value);
  };
  
  const handleDivisionChange = (e) => {
    const newDivision = e.target.value;
    setSelectedDivision(newDivision);
    
    // If division is not Homegrown, reset competition to 'League' (default)
    if (newDivision !== 'Homegrown Division') {
      setSelectedCompetition('League');
    }
    
    // Reset conference selection when division changes
    setSelectedConference('all');
  };
  
  const handleSeasonChange = (e) => {
    setSelectedSeason(e.target.value);
  };
  
  const handleCompetitionChange = (e) => {
    setSelectedCompetition(e.target.value);

    // Conference ids are per (name × competition), so the one selected under
    // League does not exist under Flex. Keeping it would send an id that resolves
    // to no conference and render an empty table — which is exactly the bug this
    // reset exists to prevent.
    setSelectedConference('all');
  };
  
  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortDirection === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';
    setSortDirection(newDirection);
    setSortColumn(column);
    
    let sortedStandings = [...standings];
    
    // Special handling for points column - apply tiebreakers
    if (column === 'PTS') {
      sortedStandings.sort((a, b) => {
        // Primary: Points (descending by default, ascending if newDirection is 'asc')
        if (a.PTS !== b.PTS) {
          return newDirection === 'asc' ? a.PTS - b.PTS : b.PTS - a.PTS;
        }
        
        // Tiebreaker: Only apply when points are equal
        // i. Head-to-head (only applicable to tie of two (2) clubs)
        // We'll handle this after the initial sort
        
        // ii. Number of Wins
        if (a.W !== b.W) {
          return b.W - a.W; // More wins is better
        }
        
        // iii. Goal differential
        if (a.GD !== b.GD) {
          return b.GD - a.GD; // Higher GD is better
        }
        
        // iv. Goals for per Match
        const aGFM = a['GF/M'] || 0;
        const bGFM = b['GF/M'] || 0;
        if (aGFM !== bGFM) {
          return bGFM - aGFM; // Higher GF/M is better
        }
        
        // v. Away goal differential per Match
        const aAwayGDM = (a.away_goal_differential || 0) / (a.MP || 1);
        const bAwayGDM = (b.away_goal_differential || 0) / (b.MP || 1);
        if (aAwayGDM !== bAwayGDM) {
          return bAwayGDM - aAwayGDM; // Higher away GD/M is better
        }
        
        // vi. Away goals for per Match
        const aAwayGFM = a.away_gf_per_match || 0;
        const bAwayGFM = b.away_gf_per_match || 0;
        if (aAwayGFM !== bAwayGFM) {
          return bAwayGFM - aAwayGFM; // Higher away GF/M is better
        }
        
        // vii. Home goal differential per Match
        const aHomeGDM = (a.home_goal_differential || 0) / (a.MP || 1);
        const bHomeGDM = (b.home_goal_differential || 0) / (b.MP || 1);
        if (aHomeGDM !== bHomeGDM) {
          return bHomeGDM - aHomeGDM; // Higher home GD/M is better
        }
        
        // viii. Home goals for per Match
        const aHomeGFM = a.home_gf_per_match || 0;
        const bHomeGFM = b.home_gf_per_match || 0;
        if (aHomeGFM !== bHomeGFM) {
          return bHomeGFM - aHomeGFM; // Higher home GF/M is better
        }
        
        return 0; // Completely equal
      });
      
    } else {
      // For other columns, use standard sorting
      sortedStandings.sort((a, b) => {
        // Handle null/undefined values for QoP/Att/Def columns - put them last (regardless of sort direction)
        if (column === 'qop_score' || column === 'att_score' || column === 'def_score') {
          const aVal = a[column];
          const bVal = b[column];
          const aIsNull = (aVal === null || aVal === undefined);
          const bIsNull = (bVal === null || bVal === undefined);
          
          // If both are null/undefined, they're equal
          if (aIsNull && bIsNull) {
            return 0;
          }
          
          // If only a is null/undefined, it goes last (return 1)
          if (aIsNull) {
            return 1;
          }
          
          // If only b is null/undefined, it goes last (return -1, meaning a comes first)
          if (bIsNull) {
            return -1;
          }
          
          // Both have values, compare normally based on sort direction
          if (aVal < bVal) return newDirection === 'asc' ? -1 : 1;
          if (aVal > bVal) return newDirection === 'asc' ? 1 : -1;
          return 0;
        }
        
        // For other columns (including rank), handle null/undefined as 0 or last
        const aVal = a[column] ?? (column === 'Rk' ? Infinity : 0);
        const bVal = b[column] ?? (column === 'Rk' ? Infinity : 0);
        
        if (aVal < bVal) return newDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return newDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    // Recalculate rankings based on sorted order
    sortedStandings = sortedStandings.map((team, index) => ({
      ...team,
      Rk: index + 1
    }));
    
    setStandings(sortedStandings);
  };
  
  const renderFormIndicator = (result) => {
    if (!result) return <Box component="span" sx={{ width: 24, display: 'inline-block' }}></Box>;
    
    const colors = {
      'W': tokens.win, // Green
      'L': tokens.loss, // Red
      'D': tokens.inkFaint  // Grey
    };
    
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: colors[result],
          color: 'white',
          fontWeight: 'bold',
          fontSize: type.label,
          marginRight: 0.5
        }}
      >
        {result}
      </Box>
    );
  };
  
  // Add error boundary

  // Show error if there's one
  if (error) {
    return (
      <Box sx={{ width: '100%', backgroundColor: '#f8f9fa', minHeight: '100vh', py: 4 }}>
        <Typography variant="h6" color="error">
          Error loading standings: {error}
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Please try refreshing the page or contact support if the problem persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      
      {/* Integrated Filters */}
      <FilterBar
        filters={[
          {
            key: 'season', label: 'Season', value: selectedSeason,
            options: seasons.map(x => ({ value: x, label: x })),
            onChange: v => setSelectedSeason(v),
            isDefault: v => v === DEFAULT_SEASON,
          },
          {
            key: 'age', label: 'Age group', value: selectedAgeGroup,
            options: AGE_GROUPS.map(x => ({ value: x, label: x })),
            onChange: v => handleAgeGroupChange({ target: { value: v } }),
            isDefault: v => v === DEFAULT_AGE_GROUP,
          },
          {
            key: 'division', label: 'Division', value: selectedDivision,
            options: (Array.isArray(divisions) ? divisions : [])
              .map(d => ({ value: d.division_name, label: shortDivision(d.division_name) })),
            onChange: v => handleDivisionChange({ target: { value: v } }),
            // "Default" here means the division the page would land on by itself.
            isDefault: v => v === (divisions.find(d => d.division_name === DEFAULT_DIVISION_NAME)
              ? 'Homegrown Division'
              : (divisions[0] || {}).division_name),
          },
          {
            key: 'conference', label: 'Conference', value: selectedConference,
            options: [{ value: 'all', label: 'All conferences' }].concat(
              (Array.isArray(conferences) ? conferences : [])
                .map(c => ({ value: String(c.id), label: c.conference_name }))
            ),
            onChange: v => handleConferenceChange({ target: { value: v } }),
            isDefault: v => v === 'all',
            nameAtDefault: true,
          },
          ...(selectedDivision === 'Homegrown Division' && U15_PLUS.includes(selectedAgeGroup) ? [{
            key: 'competition', label: 'Competition', value: selectedCompetition,
            options: [
              { value: 'League', label: 'League' },
              { value: 'MLS NEXT Flex', label: 'Flex' },
            ],
            onChange: v => handleCompetitionChange({ target: { value: v } }),
            isDefault: v => v === 'League',
          }] : []),
        ]}
      />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : standings.length === 0 ? (
        /* Before a season kicks off there are teams and fixtures but no
           results, so a standings table is legitimately empty. Saying "no teams
           found" there blames the filters for something the calendar explains. */
        <Box sx={{ my: 5, textAlign: 'center', px: 2 }}>
          {!isSeasonInProgress(selectedSeason) ? (
            <>
              <Typography variant="h6" color="textSecondary">
                {`No results yet for ${selectedSeason}`}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                The table fills in as matches are played.
              </Typography>
            </>
          ) : (
            <Typography variant="h6" color="textSecondary">
              {`No teams found for ${selectedDivision} in ${selectedAgeGroup}`}
            </Typography>
          )}
        </Box>
      ) : (
        /* Inset to the same gutter as the filter band above and every card
           elsewhere. The column widths below were trimmed to suit, so the widest
           case (U13/U14, 18 columns) still fits without a scrollbar. */
        <Box sx={{
          mx: isMobile ? 1.5 : 3,
          backgroundColor: 'white',
          borderRadius: 0,
          overflow: 'hidden',
          border: 'none',
          boxShadow: 'none'
        }}>
          {isMobile && (
            <Box sx={{ px: 1.5, pt: 1, pb: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => setShowAllStats(v => !v)}
                sx={{
                  minHeight: 44,
                  textTransform: 'none',
                  fontSize: type.bodySm,
                  color: tokens.navy,
                  fontWeight: 600
                }}
              >
                {showAllStats ? 'Show key stats' : 'Show all stats \u2192'}
              </Button>
            </Box>
          )}
          <Box sx={{ position: 'relative' }}>
          {canScrollRight && (
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: 36,
                pointerEvents: 'none',
                zIndex: 4,
                background: `linear-gradient(to right, rgba(255,255,255,0), ${tokens.surface})`,
              }}
            />
          )}
          <Box
            ref={scrollerRef}
            sx={{
              overflowX: 'auto',
              // The last column used to sit flush against the content edge.
              // Padding here rather than on the cell so the gutter is outside
              // the table and the column widths stay exact.
              pr: { xs: 0, md: 1.5 },
            }}
          >
            <Table
              sx={{
                minWidth: compact ? '100%' : (isMobile ? 900 : '100%'),
                tableLayout: 'fixed',
                width: '100%',
                '& th, & td': { 
                  padding: isMobile ? '10px 4px' : '16px 8px',
                  borderBottom: '1px solid #f1f3f4',
                  borderRight: 'none'
                },
                '& th': {
                  backgroundColor: 'transparent',
                  fontWeight: 600,
                  color: tokens.inkMuted,
                  fontSize: isMobile ? type.label : type.caption,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: 'center'
                },
                '& td': {
                  fontSize: isMobile ? type.bodySm : type.body,
                  color: tokens.ink
                }
              }}
              size="small"
            >
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{
                      width: 34,
                      position: 'sticky',
                      left: 0,
                      zIndex: 6,
                      // `& th { background: transparent }` on the Table outranks a plain
                      // backgroundColor here, which let scrolling headers show through.
                      '&&': { backgroundColor: tokens.surface },
                      textAlign: 'center',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort('Rk')}
                  >
                    #
                  </TableCell>
                  <TableCell sx={{
                    width: 32,
                    padding: '0 !important',
                    position: 'sticky',
                    left: 34,
                    zIndex: 6,
                    '&&': { backgroundColor: tokens.surface },
                    borderRight: `1px solid ${tokens.ruleSoft}`,
                  }} />
                  <TableCell sx={{
                    width: 158,
                    maxWidth: 158,
                    // The Table's own `& th` rule sets center alignment and outranks a
                    // plain textAlign here; `&&` doubles specificity so the header
                    // lines up with the left-aligned names beneath it.
                    '&&': { textAlign: 'left' },
                  }}>
                    Team
                  </TableCell>
                  <TableCell 
                    sx={{ width: compact ? '8.5%' : (isMobile ? 32 : W_STAT), textAlign: 'center', cursor: 'pointer' }}
                    onClick={() => handleSort('MP')}
                  >MP</TableCell>
                  <TableCell 
                    sx={{ width: compact ? '8.5%' : (isMobile ? 32 : W_STAT), textAlign: 'center', cursor: 'pointer' }}
                    onClick={() => handleSort('W')}
                  >W</TableCell>
                  <TableCell 
                    sx={{ width: compact ? '8.5%' : (isMobile ? 32 : W_STAT), textAlign: 'center', cursor: 'pointer' }}
                    onClick={() => handleSort('D')}
                  >D</TableCell>
                  <TableCell 
                    sx={{ width: compact ? '8.5%' : (isMobile ? 32 : W_STAT), textAlign: 'center', cursor: 'pointer' }}
                    onClick={() => handleSort('L')}
                  >L</TableCell>
                  {/* Flex only. Level at full time goes to penalties there, and the
                      shootout is worth the difference between 1 point and 2 — so
                      without this column two sides with the same W-D-L show
                      different totals and the table looks wrong. League has no
                      shootouts and is not given a column of dashes. */}
                  {showPk && (
                    <TableCell
                      sx={{ width: isMobile ? 40 : W_RATE, textAlign: 'center', cursor: 'pointer' }}
                      onClick={() => handleSort('pk_wins')}
                      title="Penalty shootouts won-lost. A shootout win is worth 2 points, a loss 1."
                    >PK</TableCell>
                  )}
                  <TableCell 
                    sx={{ width: isMobile ? 32 : W_STAT, textAlign: 'center', cursor: 'pointer', ...secondaryCol }}
                    onClick={() => handleSort('GF')}
                  >GF</TableCell>
                  <TableCell 
                    sx={{ width: isMobile ? 32 : W_STAT, textAlign: 'center', cursor: 'pointer', ...secondaryCol }}
                    onClick={() => handleSort('GA')}
                  >GA</TableCell>
                  <TableCell 
                    sx={{ width: compact ? '11%' : (isMobile ? 40 : W_RATE), textAlign: 'center', cursor: 'pointer' }}
                    onClick={() => handleSort('GD')}
                  >DIFF</TableCell>
                  <TableCell 
                    sx={{ width: isMobile ? 32 : W_STAT, textAlign: 'center', cursor: 'pointer', ...secondaryCol }}
                    onClick={() => handleSort('CS')}
                  >CS</TableCell>
                  <TableCell 
                    sx={{ width: isMobile ? 40 : W_RATE, textAlign: 'center', cursor: 'pointer', ...secondaryCol }}
                    onClick={() => handleSort('GF/M')}
                  >GF/M</TableCell>
                  <TableCell 
                    sx={{ width: isMobile ? 40 : W_RATE, textAlign: 'center', cursor: 'pointer', ...secondaryCol }}
                    onClick={() => handleSort('GA/M')}
                  >GA/M</TableCell>
                  <TableCell 
                    sx={{ width: compact ? '9.5%' : (isMobile ? 38 : W_SCORE), textAlign: 'center', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('PTS')}
                  >PTS</TableCell>
                  {/* Show Att and Def for Homegrown Division U13 and U14 only */}
                  {!compact && selectedDivision === 'Homegrown Division' && (selectedAgeGroup === 'U13' || selectedAgeGroup === 'U14') && (
                    <>
                      <TableCell 
                        sx={{ 
                          width: isMobile ? 60 : W_SCORE, 
                          textAlign: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleSort('att_score')}
                      >
                        Att
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          width: isMobile ? 60 : W_SCORE, 
                          textAlign: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleSort('def_score')}
                      >
                        Def
                      </TableCell>
                    </>
                  )}
                  {/* QoP exists only for U13/U14 — never rendered above that. */}
                  {!compact && selectedDivision === 'Homegrown Division'
                    && QOP_AGE_GROUPS.includes(selectedAgeGroup) && (
                    <TableCell 
                      sx={{ 
                        width: isMobile ? 60 : W_SCORE, 
                        textAlign: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleSort('qop_score')}
                    >
                      QoP
                    </TableCell>
                  )}
                  <TableCell sx={{ width: isMobile ? 80 : W_FORM, textAlign: 'center', ...secondaryCol }}>
                    Last 5
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(standings) ? standings.map((team, index) => {
                  try {
                    return (
                  <TableRow
                    key={index}
                    sx={{
                      '&:hover': { 
                        backgroundColor: '#f8f9fa'
                      }
                    }}
                  >
                    <TableCell sx={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: tokens.inkMuted,
                      width: 34,
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      '&&': { backgroundColor: tokens.surface },
                    }}>
                      {team.Rk}
                    </TableCell>
                    {/* Crest keeps its own pinned column; the name scrolls away. */}
                    <TableCell sx={{
                      width: 32,
                      padding: '0 !important',
                      position: 'sticky',
                      left: 34,
                      zIndex: 3,
                      '&&': { backgroundColor: tokens.surface },
                      borderRight: `1px solid ${tokens.ruleSoft}`,
                    }}>
                      {team.logo_url && (
                        <Avatar
                          src={team.logo_url}
                          alt={team.team}
                          sx={{ width: 22, height: 22, margin: '0 auto' }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{
                      textAlign: 'left',
                      width: 158,
                      maxWidth: 158,
                      overflow: 'hidden',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                      {isMobile ? (
                        <Tooltip title={team.team} disableInteractive={false} arrow>
                          <Box
                            component={Link}
                            to={`/team/${team.team_id}`}
                            sx={{
                              textDecoration: 'none',
                              color: tokens.ink,
                              fontWeight: 600,
                              fontSize: type.caption,
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                              ...TAP_TARGET
                            }}
                          >
                            {team.team}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Link
                          to={`/team/${team.team_id}`}
                          style={{
                            textDecoration: 'none',
                            color: tokens.ink,
                            fontWeight: 600,
                            fontSize: type.bodySm,
                            display: 'block'
                          }}
                        >
                          {team.team}
                        </Link>
                      )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted }}>{team.MP}</TableCell>
                    <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted }}>{team.W}</TableCell>
                    <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted }}>{team.D}</TableCell>
                    <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted }}>{team.L}</TableCell>
                    {showPk && (
                      <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted }}>
                        {team.pk_wins || team.pk_losses
                          ? `${team.pk_wins}-${team.pk_losses}`
                          : '\u2013'}
                      </TableCell>
                    )}
                    <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted, ...secondaryCol }}>{team.GF}</TableCell>
                    <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted, ...secondaryCol }}>{team.GA}</TableCell>
                    <TableCell
                      style={{
                        textAlign: 'center',
                        color: team.GD > 0 ? tokens.win : team.GD < 0 ? tokens.loss : tokens.inkMuted
                      }}
                    >
                      {team.GD > 0 ? `+${team.GD}` : team.GD}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted, ...secondaryCol }}>{team.CS}</TableCell>
                    <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted, ...secondaryCol }}>{team['GF/M']?.toFixed(2) || '0.00'}</TableCell>
                    <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted, ...secondaryCol }}>{team['GA/M']?.toFixed(2) || '0.00'}</TableCell>
                    <TableCell sx={{ 
                      textAlign: 'center', 
                      color: tokens.ink
                    }}>
                      {team.PTS}
                    </TableCell>
                    {/* Show Att and Def for Homegrown Division U13 and U14 only */}
                    {!compact && selectedDivision === 'Homegrown Division' && (selectedAgeGroup === 'U13' || selectedAgeGroup === 'U14') && (
                      <>
                        <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted }}>
                          {team.att_score ? team.att_score.toFixed(1) : '—'}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted }}>
                          {team.def_score ? team.def_score.toFixed(1) : '—'}
                        </TableCell>
                      </>
                    )}
                    {/* QoP exists only for U13/U14 — never rendered above that. */}
                    {!compact && selectedDivision === 'Homegrown Division'
                      && QOP_AGE_GROUPS.includes(selectedAgeGroup) && (
                      <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted }}>
                        {team.qop_score ? team.qop_score.toFixed(1) : '—'}
                      </TableCell>
                    )}
                    <TableCell sx={{ textAlign: 'center', color: tokens.inkMuted, ...secondaryCol }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        gap: 0.5,
                        flexWrap: 'nowrap'
                      }}>
                        {team.last_five && Array.isArray(team.last_five) ? (
                          <>
                            {team.last_five.map((result, idx) => (
                              <Box
                                key={idx}
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: isMobile ? 16 : 18,
                                  height: isMobile ? 16 : 18,
                                  borderRadius: '50%',
                                  backgroundColor: result === 'W' ? tokens.win : result === 'L' ? tokens.loss : tokens.inkMuted,
                                  fontSize: isMobile ? type.label : type.caption,
                                  color: 'white',
                                  fontWeight: 'bold',
                                  flexShrink: 0
                                }}
                              >
                                {result}
                              </Box>
                            ))}
                            {/* Fill remaining slots with dashes if less than 5 games */}
                            {Array.from({ length: Math.max(0, 5 - team.last_five.length) }).map((_, idx) => (
                              <Box
                                key={`dash-${idx}`}
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: isMobile ? 16 : 18,
                                  height: isMobile ? 16 : 18,
                                  borderRadius: '50%',
                                  backgroundColor: tokens.rule,
                                  fontSize: isMobile ? type.label : type.caption,
                                  color: tokens.inkMuted,
                                  fontWeight: 'bold',
                                  flexShrink: 0
                                }}
                              >
                                —
                              </Box>
                            ))}
                          </>
                        ) : (
                          // Show 5 dashes if no last_five data
                          Array.from({ length: 5 }).map((_, idx) => (
                            <Box
                              key={`no-data-${idx}`}
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: isMobile ? 16 : 18,
                                height: isMobile ? 16 : 18,
                                borderRadius: '50%',
                                backgroundColor: tokens.rule,
                                fontSize: isMobile ? type.label : type.caption,
                                color: tokens.inkMuted,
                                fontWeight: 'bold',
                                flexShrink: 0
                              }}
                            >
                              —
                            </Box>
                          ))
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                    );
                  } catch (error) {
                    console.error('Error rendering team row:', error, team);
                    return null;
                  }
                }) : []}
              </TableBody>
            </Table>
          </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Standings;
